import {
	BrushCleaning,
	Building2,
	Car,
	CircleHelp,
	Clapperboard,
	GraduationCap,
	HeartHandshake,
	House,
	Landmark,
	Monitor,
	Palette,
	Plane,
	Scale,
	ShoppingBasket,
	Smile,
	Sparkles,
	SquareActivity,
	Squirrel,
	Stethoscope,
	Truck,
	UmbrellaOff,
	Utensils,
	WalletMinimal,
	Wrench
} from 'lucide-react';
import { runtimeConfig } from '@/constants';
import {
	type StepperIndustryGroup,
	StepperIndustryGroupKey,
	StepperIndustryKey,
	StepperIndustryValue,
	type StepperSetupStep,
	StepperSetupStepKey
} from './types';

type StepperConfiguredIndustryKey = Exclude<
	StepperIndustryKey,
	StepperIndustryKey.AutoDetect
>;

export const STEPPER_FIRST_STEP_NUMBER = 1;
export const STEPPER_PROGRESS_COMPLETE_PERCENT = 100;
export const STEPPER_PROGRESS_PENDING_PERCENT = 95;
export const STEPPER_PROGRESS_PENDING_TIMEOUT_MS = 30000;
export const STEPPER_PORTAL_HANDOFF_DELAY_MS = 10000;
export const STEPPER_STEP_TRANSITION_PROGRESS_TIMEOUT_MS = runtimeConfig.isE2E
	? 1000
	: 10000;
export const STEPPER_AGENT_AVATAR_URL =
	'https://storage.googleapis.com/newo-static-files/agent-creator/ionos/leon-elldot-f6HbVnGtNnY-unsplash%201.png';
export const STEPPER_FULL_SIZE_AGENT_AVATAR_URL =
	'https://storage.googleapis.com/newo-static-files/agent-creator/ionos/b59eb39a60de742a69f6f820de26f97f88c571fa.png';
export const STEPPER_TEST_CALL_MOBILE_MEDIA_QUERY = '(max-width: 768px)';

export const STEPPER_EMPTY_PHONE_PLACEHOLDER = '---';
export const STEPPER_PORTAL_ONBOARDING_TOUR_QUERY_PARAM = 'onboarding_tour';
export const STEPPER_PORTAL_ONBOARDING_TOUR_QUERY_VALUE = 'portal';
export const STEPPER_PORTAL_SETTINGS_ATTRIBUTE_QUERY_PARAM = 'attribute_idn';
export const STEPPER_AGENT_NAME_ATTRIBUTE_IDN =
	'project_representative_agent_name';

export const STEPPER_INDUSTRY_VALUE_BY_KEY: Partial<
	Record<StepperIndustryKey, StepperIndustryValue>
> = {
	[StepperIndustryKey.Automotive]: StepperIndustryValue.Sales,
	[StepperIndustryKey.Catering]: StepperIndustryValue.Catering,
	[StepperIndustryKey.CleaningServices]: StepperIndustryValue.Cleaning,
	[StepperIndustryKey.ConstructionContracting]: StepperIndustryValue.Sales,
	[StepperIndustryKey.ConsultingProfessionalServices]:
		StepperIndustryValue.Sales,
	[StepperIndustryKey.DentalOrthodontics]: StepperIndustryValue.Dental,
	[StepperIndustryKey.DesignPhotographyCreative]: StepperIndustryValue.Sales,
	[StepperIndustryKey.ECommerceRetail]: StepperIndustryValue.ECommerce,
	[StepperIndustryKey.EducationNonprofit]: StepperIndustryValue.Education,
	[StepperIndustryKey.EventsEntertainmentTicketing]: StepperIndustryValue.Sales,
	[StepperIndustryKey.FinancialServices]: StepperIndustryValue.Sales,
	[StepperIndustryKey.FuneralServices]: StepperIndustryValue.Funeral,
	[StepperIndustryKey.GeneralReceptionist]: StepperIndustryValue.General,
	[StepperIndustryKey.Healthcare]: StepperIndustryValue.Dental,
	[StepperIndustryKey.HomeServices]: StepperIndustryValue.HomeService,
	[StepperIndustryKey.HospitalityHotels]: StepperIndustryValue.Hospitality,
	[StepperIndustryKey.Insurance]: StepperIndustryValue.Insurance,
	[StepperIndustryKey.InvestmentCapital]: StepperIndustryValue.Investment,
	[StepperIndustryKey.LegalLawFirms]: StepperIndustryValue.Legal,
	[StepperIndustryKey.LogisticsTransportation]: StepperIndustryValue.Logistics,
	[StepperIndustryKey.ManufacturingIndustrialServices]:
		StepperIndustryValue.Manufacturing,
	[StepperIndustryKey.MarketingMedia]: StepperIndustryValue.Sales,
	[StepperIndustryKey.Other]: StepperIndustryValue.Sales,
	[StepperIndustryKey.PetServices]: StepperIndustryValue.PetServices,
	[StepperIndustryKey.PropertyManagement]:
		StepperIndustryValue.PropertyManagement,
	[StepperIndustryKey.RealEstate]: StepperIndustryValue.Sales,
	[StepperIndustryKey.Restaurants]: StepperIndustryValue.Restaurant,
	[StepperIndustryKey.TechnologyIt]: StepperIndustryValue.Sales,
	[StepperIndustryKey.TourismTravel]: StepperIndustryValue.Sales,
	[StepperIndustryKey.WellnessBeautyFitnessSports]: StepperIndustryValue.Beauty
} satisfies Record<StepperConfiguredIndustryKey, StepperIndustryValue>;

export const STEPPER_SETUP_STEPS: StepperSetupStep[] = [
	{
		key: StepperSetupStepKey.ExtractWebsiteContent
	},
	{
		key: StepperSetupStepKey.CustomizeGreeting
	},
	{
		key: StepperSetupStepKey.CallSummariesTransfers
	},
	{
		key: StepperSetupStepKey.MakeTestCall
	}
];

export const STEPPER_INDUSTRY_GROUPS: [
	StepperIndustryGroup,
	StepperIndustryGroup
] = [
	{
		key: StepperIndustryGroupKey.Primary,
		industries: [
			{
				icon: Monitor,
				iconClassName: 'text-[#8A5CFF]',
				key: StepperIndustryKey.TechnologyIt
			},
			{
				icon: HeartHandshake,
				iconClassName: 'text-[#10A968]',
				key: StepperIndustryKey.ConsultingProfessionalServices
			},
			{
				icon: Wrench,
				iconClassName: 'text-[#8A97AA]',
				key: StepperIndustryKey.ConstructionContracting
			},
			{
				icon: SquareActivity,
				iconClassName: 'text-[#FF334A]',
				key: StepperIndustryKey.Healthcare
			},
			{
				icon: Sparkles,
				iconClassName: 'text-[#22A7F2]',
				key: StepperIndustryKey.MarketingMedia
			},
			{
				icon: WalletMinimal,
				iconClassName: 'text-[#0FA954]',
				key: StepperIndustryKey.FinancialServices
			}
		]
	},
	{
		key: StepperIndustryGroupKey.Additional,
		industries: [
			{
				icon: House,
				iconClassName: 'text-[#344054]',
				key: StepperIndustryKey.HomeServices
			},
			{
				icon: Car,
				iconClassName: 'text-[#2188FF]',
				key: StepperIndustryKey.Automotive
			},
			{
				icon: BrushCleaning,
				iconClassName: 'text-[#3B8DFF]',
				key: StepperIndustryKey.CleaningServices
			},
			{
				icon: Palette,
				iconClassName: 'text-[#D51BB3]',
				key: StepperIndustryKey.DesignPhotographyCreative
			},
			{
				icon: Building2,
				iconClassName: 'text-[#10A968]',
				key: StepperIndustryKey.RealEstate
			},
			{
				icon: Sparkles,
				iconClassName: 'text-[#22A7F2]',
				key: StepperIndustryKey.WellnessBeautyFitnessSports
			},
			{
				icon: Smile,
				iconClassName: 'text-[#2764BE]',
				key: StepperIndustryKey.GeneralReceptionist
			},
			{
				icon: CircleHelp,
				iconClassName: 'text-[#2764BE]',
				key: StepperIndustryKey.Other
			},
			{
				icon: GraduationCap,
				iconClassName: 'text-[#FF8A00]',
				key: StepperIndustryKey.EducationNonprofit
			},
			{
				icon: Truck,
				iconClassName: 'text-[#8A5CFF]',
				key: StepperIndustryKey.LogisticsTransportation
			},
			{
				icon: ShoppingBasket,
				iconClassName: 'text-[#8896AA]',
				key: StepperIndustryKey.ECommerceRetail
			},
			{
				icon: Utensils,
				iconClassName: 'text-[#EF8A21]',
				key: StepperIndustryKey.Restaurants
			},
			{
				icon: Building2,
				iconClassName: 'text-[#0081FF]',
				key: StepperIndustryKey.HospitalityHotels
			},
			{
				icon: Scale,
				iconClassName: 'text-[#FF334A]',
				key: StepperIndustryKey.LegalLawFirms
			},
			{
				icon: WalletMinimal,
				iconClassName: 'text-[#2188FF]',
				key: StepperIndustryKey.InvestmentCapital
			},
			{
				icon: Plane,
				iconClassName: 'text-[#3B8DFF]',
				key: StepperIndustryKey.TourismTravel
			},
			{
				icon: Squirrel,
				iconClassName: 'text-[#D51BB3]',
				key: StepperIndustryKey.PetServices
			},
			{
				icon: Clapperboard,
				iconClassName: 'text-[#8A5CFF]',
				key: StepperIndustryKey.EventsEntertainmentTicketing
			},
			{
				icon: Wrench,
				iconClassName: 'text-[#3EB6FB]',
				key: StepperIndustryKey.ManufacturingIndustrialServices
			},
			{
				icon: Utensils,
				iconClassName: 'text-[#EF8A21]',
				key: StepperIndustryKey.Catering
			},
			{
				icon: Stethoscope,
				iconClassName: 'text-[#2DB9FF]',
				key: StepperIndustryKey.DentalOrthodontics
			},
			{
				icon: UmbrellaOff,
				iconClassName: 'text-[#36A905]',
				key: StepperIndustryKey.Insurance
			},
			{
				icon: Landmark,
				iconClassName: 'text-[#98A2B3]',
				key: StepperIndustryKey.PropertyManagement
			},
			{
				icon: Building2,
				iconClassName: 'text-[#344054]',
				key: StepperIndustryKey.FuneralServices
			}
		]
	}
];
